import { useIsMobile } from '@commander/mobile-view';
import {
  Button,
  Sheet,
  SheetClose,
  SheetContent,
  SheetContentNoClose,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@commander/ui';
import {
  RiAlertLine,
  RiChatNewLine,
  RiCloseLine,
  RiHistoryLine,
  RiPencilLine,
  RiSparklingLine,
} from '@remixicon/react';
import type { UIMessage } from 'ai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { COMMANDER_GO_DEFAULTS } from '../constants.js';
import { ChatSendContext, type ChatSendFn } from '../hooks/ChatSendContext.js';
import { CommanderGoAnalyticsContext } from '../hooks/useCommanderGoAnalytics.js';
import type { ThreadSummary } from '../types.js';
import { ChatInput } from './ChatInput.js';
import { MessageList } from './MessageList.js';
import { ThreadList } from './ThreadList.js';

interface CommanderGoPanelProps {
  isOpen: boolean;
  onClose: () => void;
  messages: UIMessage[];
  isLoading: boolean;
  status?: 'submitted' | 'streaming' | 'ready' | 'error';
  error: Error | undefined;
  onSend: ChatSendFn;
  onNewChat: () => void;
  onRetry: () => void;
  appContext?: 'crm' | 'prospecting';
  // Thread management
  threads: ThreadSummary[];
  showThreadList: boolean;
  onToggleThreadList: () => void;
  onSelectThread: (id: string) => void;
  onDeleteThread: (id: string) => void;
  onRenameThread: (id: string, title: string) => void;
  onTogglePinThread: (id: string) => void;
  onClearAllThreads: () => void;
  activeThreadId: string;
  hasMoreThreads?: boolean;
  onLoadMoreThreads?: () => void;
  // Message pagination
  onLoadOlder?: () => void;
  hasPreviousMessages?: boolean;
  isLoadingOlder?: boolean;
}

export function CommanderGoPanel({
  isOpen,
  onClose,
  messages,
  isLoading,
  error,
  onSend,
  onNewChat,
  onRetry,
  appContext,
  threads,
  showThreadList,
  onToggleThreadList,
  onSelectThread,
  onDeleteThread,
  onRenameThread,
  onTogglePinThread,
  onClearAllThreads,
  activeThreadId,
  hasMoreThreads,
  onLoadMoreThreads,
  onLoadOlder,
  hasPreviousMessages,
  isLoadingOlder,
}: CommanderGoPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [errorDismissed, setErrorDismissed] = useState(false);
  const isMobile = useIsMobile();
  const analyticsCtx = useMemo(
    () => ({ threadId: activeThreadId, appContext: appContext ?? 'unknown' }),
    [activeThreadId, appContext]
  );

  // Reset dismiss when a new error appears
  // biome-ignore lint/correctness/useExhaustiveDependencies: error prop change must reset dismissed state
  useEffect(() => {
    setErrorDismissed(false);
  }, [error]);

  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const titleInputRef = useRef<HTMLInputElement>(null);

  const activeThread = threads.find((t) => t.id === activeThreadId);
  const activeTitle = activeThread?.title;
  const showTitle = !!activeTitle && messages.length > 0;

  const startEditingTitle = useCallback(() => {
    if (!showTitle) return;
    setTitleDraft(activeTitle ?? '');
    setEditingTitle(true);
  }, [showTitle, activeTitle]);

  const saveTitle = useCallback(() => {
    const trimmed = titleDraft.trim();
    if (trimmed && trimmed !== activeTitle) {
      onRenameThread(activeThreadId, trimmed);
    }
    setEditingTitle(false);
  }, [titleDraft, activeTitle, activeThreadId, onRenameThread]);

  useEffect(() => {
    if (editingTitle) {
      titleInputRef.current?.focus();
      titleInputRef.current?.select();
    }
  }, [editingTitle]);

  const panelContent = (
    <CommanderGoAnalyticsContext.Provider value={analyticsCtx}>
      <SheetHeader className="flex flex-row items-center justify-between border-b px-6 py-4 shadow-sm">
        <div className="min-w-0 flex-1">
          <SheetTitle className="flex items-center gap-2">
            <RiSparklingLine className="h-4 w-4 shrink-0 text-primary" />
            <span className="shrink-0">{COMMANDER_GO_DEFAULTS.title}</span>
            {showTitle && (
              <>
                <span className="text-muted-foreground font-normal">/</span>
                {editingTitle ? (
                  <input
                    ref={titleInputRef}
                    type="text"
                    value={titleDraft}
                    onChange={(e) => setTitleDraft(e.target.value)}
                    onBlur={saveTitle}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        saveTitle();
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        setEditingTitle(false);
                      }
                    }}
                    // Fill the remaining space in the header up to the action
                    // buttons so the field keeps the same footprint as the
                    // title text it replaces.
                    className="flex-1 min-w-0 text-sm font-normal bg-transparent border border-border rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-ring"
                    maxLength={100}
                  />
                ) : (
                  <button
                    type="button"
                    className="group/title flex items-center gap-1 min-w-0 cursor-pointer bg-transparent border-none p-0"
                    onClick={startEditingTitle}
                  >
                    <span className="text-sm font-normal text-muted-foreground truncate">
                      {activeTitle}
                    </span>
                    <RiPencilLine className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover/title:opacity-100 transition-opacity shrink-0" />
                  </button>
                )}
              </>
            )}
          </SheetTitle>
          <SheetDescription className="sr-only">
            AI chat assistant for querying your data
          </SheetDescription>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={onNewChat}
            disabled={isLoading || messages.length === 0}
            title="New chat"
          >
            <RiChatNewLine className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={onToggleThreadList} title="Chat history">
            <RiHistoryLine className="h-4 w-4" />
          </Button>
          <SheetClose asChild>
            <Button variant="ghost" size="icon" title="Close">
              <RiCloseLine className="h-4 w-4" />
            </Button>
          </SheetClose>
        </div>
      </SheetHeader>

      {error && !errorDismissed && (
        <div className="flex items-start gap-2 border-b bg-destructive/10 px-4 py-2 text-sm text-destructive">
          <RiAlertLine className="h-4 w-4 shrink-0 mt-0.5" />
          <p className="flex-1 min-w-0 line-clamp-2">{error.message}</p>
          <button
            type="button"
            className="shrink-0 mt-0.5 hover:text-destructive/70 transition-colors"
            onClick={() => setErrorDismissed(true)}
          >
            <RiCloseLine className="h-4 w-4" />
          </button>
        </div>
      )}

      {showThreadList ? (
        <ThreadList
          threads={threads}
          activeThreadId={activeThreadId}
          onSelectThread={onSelectThread}
          onDeleteThread={onDeleteThread}
          onRenameThread={onRenameThread}
          onTogglePinThread={onTogglePinThread}
          onClearAllThreads={onClearAllThreads}
          onBack={onToggleThreadList}
          portalContainer={panelRef.current}
          hasMoreThreads={hasMoreThreads}
          onLoadMore={onLoadMoreThreads}
        />
      ) : (
        <ChatSendContext.Provider value={onSend}>
          <MessageList
            messages={messages}
            isLoading={isLoading}
            onRetry={onRetry}
            onSend={onSend}
            appContext={appContext}
            onLoadOlder={onLoadOlder}
            hasPreviousMessages={hasPreviousMessages}
            isLoadingOlder={isLoadingOlder}
          />
          <ChatInput key={activeThreadId} onSend={onSend} disabled={isLoading} />
        </ChatSendContext.Provider>
      )}
    </CommanderGoAnalyticsContext.Provider>
  );

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      {isMobile ? (
        <SheetContent
          ref={panelRef}
          side="bottom"
          className="flex flex-col rounded-none gap-0 [&>button]:hidden overflow-hidden p-0 outline-none focus:outline-none focus-visible:outline-none"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            height: '100dvh',
            maxHeight: '100dvh',
            width: '100vw',
            maxWidth: '100vw',
          }}
        >
          {panelContent}
        </SheetContent>
      ) : (
        <SheetContentNoClose
          ref={panelRef}
          side="right"
          className="flex flex-col overflow-hidden p-0 outline-none focus:outline-none focus-visible:outline-none"
          style={{ maxWidth: 700 }}
        >
          {panelContent}
        </SheetContentNoClose>
      )}
    </Sheet>
  );
}
